<template>
  <div class="page-wrap">

    <!-- QuickBooks sync overlay -->
    <div class="sync-overlay" v-if="isQuickBookSyncing">
      <div class="sync-spinner"></div>
      <span>Syncing with QuickBooks…</span>
    </div>

    <div class="panel-card">

      <!-- Header -->
      <div class="panel-header">
        <div class="panel-header__icon"><i class="fa fa-building"></i></div>
        <div class="panel-header__text">
          <div class="panel-header__title">Companies</div>
          <div class="panel-header__sub">Manage registered companies and their settings</div>
        </div>
        <div class="panel-header__action" v-if="currentUser.u.roles === 'admin'">
          <router-link :to="{ name: 'client.master.company.create' }" class="btn-add">
            <i class="fa fa-plus"></i> Add Company
          </router-link>
        </div>
      </div>

      <!-- Toolbar -->
      <div class="toolbar">
        <div class="search-wrap">
          <i class="fa fa-search search-icon"></i>
          <input v-model="searchInput" type="text" class="search-input" placeholder="Search company name…" />
        </div>
        <div class="toolbar__meta" v-if="!loadingData">
          {{ filteredMasterCompanies.length }} {{ filteredMasterCompanies.length === 1 ? 'company' : 'companies' }}
        </div>
      </div>

      <!-- Loading -->
      <div class="state-box" v-if="loadingData">
        <div class="state-spinner"></div>
        <span>Loading companies…</span>
      </div>

      <!-- Empty -->
      <div class="state-box state-box--muted" v-else-if="filteredMasterCompanies.length === 0">
        <i class="fa fa-building state-box__icon"></i>
        <div class="state-box__title">No companies found</div>
        <div class="state-box__sub">{{ searchInput ? 'Try a different search term' : 'Add the first company to get
          started' }}</div>
      </div>

      <!-- Table -->
      <div class="table-wrap" v-else>
        <table class="co-table">
          <thead>
            <tr>
              <th class="th-sm">#</th>
              <th class="th-name">Company Name</th>
              <th class="th-contact">Contact Name</th>
              <th class="th-flag">Use in Archive</th>
              <th class="th-flag">Status</th>
              <th class="th-flag">QuickBooks</th>
              <th class="th-action" v-if="currentUser.u.roles === 'admin'">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in pagedCompanies" :key="item.id">
              <td class="td-sm">{{ (currentPage - 1) * perPage + index + 1 }}</td>
              <td class="td-name">{{ item.company_name }}</td>
              <td class="td-contact">{{ item.contact_name || '—' }}</td>
              <td class="td-flag">
                <span class="bool-chip" :class="item.use_in_archive ? 'chip--green' : 'chip--muted'">
                  <i :class="item.use_in_archive ? 'fa fa-check' : 'fa fa-times'"></i>
                  {{ item.use_in_archive ? 'Yes' : 'No' }}
                </span>
              </td>
              <td class="td-flag">
                <span class="bool-chip" :class="item.is_active ? 'chip--green' : 'chip--red'">
                  <i :class="item.is_active ? 'fa fa-check' : 'fa fa-times'"></i>
                  {{ item.is_active ? 'Active' : 'Inactive' }}
                </span>
              </td>
              <td class="td-flag">
                <span class="bool-chip" :class="item.is_quickbooks_synced ? 'chip--blue' : 'chip--muted'">
                  <i :class="item.is_quickbooks_synced ? 'fa fa-check' : 'fa fa-times'"></i>
                  {{ item.is_quickbooks_synced ? 'Synced' : 'Not Synced' }}
                </span>
              </td>
              <td class="td-action" v-if="currentUser.u.roles === 'admin'">
                <button class="dots-btn" @click.stop="toggleMenu(index, $event)">
                  <i class="fa fa-ellipsis-v"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="pagination-bar" v-if="filteredMasterCompanies.length > perPage">
        <span class="pg-info">
          Showing {{ (currentPage - 1) * perPage + 1 }}–{{ Math.min(currentPage * perPage,
            filteredMasterCompanies.length) }}
          of {{ filteredMasterCompanies.length }}
        </span>
        <div class="pg-controls">
          <button class="pg-btn" :disabled="currentPage === 1" @click="currentPage = 1">
            <i class="fa fa-angle-double-left"></i>
          </button>
          <button class="pg-btn" :disabled="currentPage === 1" @click="currentPage--">
            <i class="fa fa-angle-left"></i>
          </button>
          <button v-for="p in visiblePages" :key="p" class="pg-btn" :class="{ active: p === currentPage }"
            @click="currentPage = p">{{ p }}</button>
          <button class="pg-btn" :disabled="currentPage === totalPages" @click="currentPage++">
            <i class="fa fa-angle-right"></i>
          </button>
          <button class="pg-btn" :disabled="currentPage === totalPages" @click="currentPage = totalPages">
            <i class="fa fa-angle-double-right"></i>
          </button>
        </div>
      </div>

    </div>

    <!-- 3-dot dropdown -->
    <div class="dot-menu" v-if="activeMenu !== null" :style="{ top: menuPos.top + 'px', right: menuPos.right + 'px' }"
      @click.stop>
      <button class="dot-item" @click="editData(menuItem.id); activeMenu = null">
        <i class="fa fa-pencil-alt"></i> Edit
      </button>
      <button class="dot-item dot-item--del" @click="openDeleteModal(menuItem.id); activeMenu = null">
        <i class="fa fa-trash"></i> Delete
      </button>
    </div>

    <!-- Delete modal -->
    <transition name="modal-fade">
      <div class="del-overlay" v-if="deleteModal.show" @click.self="deleteModal.show = false">
        <div class="del-box">
          <div class="del-icon"><i class="fa fa-trash"></i></div>
          <div class="del-title">Delete Company</div>
          <div class="del-sub">This action cannot be undone. The company will be permanently removed.</div>
          <div class="del-actions">
            <button class="del-cancel" @click="deleteModal.show = false">Cancel</button>
            <button class="del-confirm" @click="confirmDelete">Delete</button>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";
import {
  FETCH_MASTER_COMPANIES,
  DELETE_MASTER_COMPANY,
  SYNC_QUICKBOOK_COMPANY,
} from "@/core/services/store/actions.type";
import { mapGetters } from "vuex";

export default {
  computed: {
    ...mapGetters(["getMasterCompanies", "currentUser"]),
    filteredMasterCompanies() {
      const q = this.searchInput.toUpperCase();
      if (!q) return this.getMasterCompanies;
      return this.getMasterCompanies.filter(i =>
        i.company_name && i.company_name.toUpperCase().includes(q)
      );
    },
    totalPages() {
      return Math.ceil(this.filteredMasterCompanies.length / this.perPage) || 1;
    },
    pagedCompanies() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredMasterCompanies.slice(start, start + this.perPage);
    },
    visiblePages() {
      const total = this.totalPages;
      const cur = this.currentPage;
      const pages = [];
      for (let i = Math.max(1, cur - 2); i <= Math.min(total, cur + 2); i++) {
        pages.push(i);
      }
      return pages;
    },
  },
  watch: {
    filteredMasterCompanies() {
      this.currentPage = 1;
    },
  },
  data() {
    return {
      breadCrumbs: [
        { id: 1, title: "Dashboard", route: "client.dashboard" },
        { id: 2, title: "Companies", route: "" },
      ],
      loadingData: false,
      searchInput: "",
      currentPage: 1,
      perPage: 5,
      isQuickBookSyncing: false,
      deleteModal: { show: false, id: null },
      activeMenu: null,
      menuPos: { top: 0, right: 0 },
      menuItem: null,
    };
  },
  created() {
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
    this.getMasterCompaniesData();
    document.addEventListener("click", this.closeMenu);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.closeMenu);
  },
  methods: {
    toggleMenu(index, event) {
      if (this.activeMenu === index) { this.activeMenu = null; return; }
      const btn = event.currentTarget;
      const r = btn.getBoundingClientRect();
      this.menuPos = { top: r.bottom + 4, right: window.innerWidth - r.right };
      this.menuItem = this.pagedCompanies[index];
      this.activeMenu = index;
    },
    closeMenu() { this.activeMenu = null; },
    openDeleteModal(id) {
      this.deleteModal = { show: true, id };
    },
    confirmDelete() {
      const { id } = this.deleteModal;
      this.deleteModal.show = false;
      this.$store.dispatch(DELETE_MASTER_COMPANY, id).then(data => {
        this.$toastr.s(data.msg);
        const idx = this.getMasterCompanies.findIndex(c => c.id === id);
        if (idx !== -1) this.getMasterCompanies.splice(idx, 1);
      });
    },
    syncQuickBooks(id) {
      this.isQuickBookSyncing = true;
      this.$store.dispatch(SYNC_QUICKBOOK_COMPANY, id)
        .then(() => {
          this.isQuickBookSyncing = false;
          this.$toastr.s("Synced Successfully");
          this.getMasterCompaniesData();
        })
        .catch(e => {
          this.isQuickBookSyncing = false;
          this.$toastr.e(e.msg);
        });
    },
    editData(id) {
      this.$router.push({ name: "client.master.company.edit", params: { id } });
    },
    getMasterCompaniesData() {
      this.loadingData = true;
      this.$store.dispatch(FETCH_MASTER_COMPANIES)
        .then(() => { this.loadingData = false; })
        .catch(() => { this.loadingData = false; });
    },
  },
};
</script>

<style lang="scss" scoped>
$font: 'Segoe UI', sans-serif;
$blue: #389ad4;
$dark: #0d1b3e;
$green: #276749;
$border: #dde2ec;

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.page-wrap {
  padding: 12px;
  font-family: $font;
  position: relative;
}

/* ── sync overlay ── */
.sync-overlay {
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgba(13, 27, 62, 0.35);
  backdrop-filter: blur(3px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #fff;
  font-size: 14px;
  font-family: $font;
}

/* ── panel card ── */
.panel-card {
  background: #fff;
  border: 1px solid $border;
  border-radius: 16px;
  overflow: clip;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
}

/* ── header ── */
.panel-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid $border;
  background: linear-gradient(135deg, #f8faff 0%, #f0f5ff 100%);

  &__icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, $blue, #2277aa);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 18px;
    flex-shrink: 0;
  }

  &__text {
    flex: 1;
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: $dark;
    line-height: 1.2;
  }

  &__sub {
    font-size: 12px;
    color: #7a8499;
    margin-top: 3px;
  }

  &__action {
    display: flex;
    align-items: center;
    gap: 8px;
  }
}

.btn-add {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 16px;
  border-radius: 8px;
  background: $blue;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  text-decoration: none;
  white-space: nowrap;
  border: none;
  transition: background 0.15s, box-shadow 0.15s;

  &:hover {
    background: #2d85bb;
    box-shadow: 0 4px 12px rgba(56, 154, 212, 0.35);
    color: #fff;
    text-decoration: none;
  }

  i {
    font-size: 11px;
  }
}

/* ── toolbar ── */
.toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  border-bottom: 1px solid $border;
  background: #fafbfd;

  &__meta {
    margin-left: auto;
    font-size: 12px;
    color: #7a8499;
  }
}

.search-wrap {
  position: relative;
  width: 280px;
}

.search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aab8;
  font-size: 12px;
  pointer-events: none;
}

.search-input {
  width: 100%;
  height: 34px;
  border: 1.5px solid $border;
  border-radius: 8px;
  padding: 0 10px 0 30px;
  font-size: 13px;
  color: $dark;
  font-family: $font;
  outline: none;
  background: #fff;
  transition: border-color 0.2s;

  &:focus {
    border-color: $blue;
    box-shadow: 0 0 0 3px rgba(56, 154, 212, 0.1);
  }

  &::placeholder {
    color: #b0bac9;
  }
}

/* ── state boxes ── */
.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 56px 24px;
  gap: 8px;
  color: #7a8499;

  &--muted .state-box__icon {
    color: #c5d0e0;
  }

  &__icon {
    font-size: 36px;
    margin-bottom: 4px;
  }

  &__title {
    font-size: 15px;
    font-weight: 600;
    color: $dark;
  }

  &__sub {
    font-size: 13px;
  }
}

.state-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #c5d8f5;
  border-top-color: $blue;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  margin-bottom: 8px;
}

/* ── table ── */
.table-wrap {
  overflow-x: auto;
  overflow-y: auto;
  max-height: calc(100vh - 220px);
}

.co-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  color: $dark;

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    padding: 10px 16px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #7a8499;
    border-bottom: 2px solid $border;
    white-space: nowrap;
    background: #fafbfd;
  }

  .th-sm {
    width: 52px;
    text-align: center;
  }

  .th-name {
    width: 26%;
  }

  .th-contact {
    width: 20%;
  }

  .th-flag {
    width: 120px;
    text-align: center;
  }

  .th-action {
    width: 60px;
    text-align: center;
  }

  td {
    padding: 11px 16px;
    border-bottom: 1px solid #f0f2f8;
    vertical-align: middle;
  }

  tr:hover td {
    background: #f8fbff;
  }

  tr:last-child td {
    border-bottom: none;
  }

  .td-sm {
    text-align: center;
    color: #a0aab8;
    font-size: 12px;
  }

  .td-name {
    font-weight: 500;
  }

  .td-contact {
    color: #5a6278;
    font-size: 12.5px;
  }

  .td-flag {
    text-align: center;
  }

  .td-action {
    text-align: center;
  }
}

/* ── pagination ── */
.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-top: 1px solid $border;
  flex-wrap: wrap;
  gap: 10px;

  .pg-info {
    font-size: 12px;
    color: #7a8499;
    white-space: nowrap;
  }

  .pg-controls {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .pg-btn {
    min-width: 32px;
    height: 32px;
    padding: 0 8px;
    border: 1.5px solid $border;
    background: #fff;
    color: #4a5568;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s;

    &:hover:not(:disabled) {
      border-color: $blue;
      color: $blue;
    }

    &:disabled {
      opacity: 0.35;
      cursor: default;
    }

    &.active {
      background: $blue;
      border-color: $blue;
      color: #fff;
    }
  }
}

/* ── bool chips ── */
.bool-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;

  &.chip--green {
    background: rgba(39, 103, 73, 0.1);
    color: $green;
  }

  &.chip--red {
    background: rgba(180, 35, 24, 0.1);
    color: #b42318;
  }

  &.chip--blue {
    background: rgba(56, 154, 212, 0.1);
    color: $blue;
  }

  &.chip--muted {
    background: #f0f3fa;
    color: #a0aab8;
  }

  i {
    font-size: 10px;
  }
}

/* ── 3-dot button ── */
.dots-btn {
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 7px;
  background: transparent;
  color: #a0aab8;
  font-size: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: #f0f3fa;
    color: $dark;
  }
}

/* ── dot dropdown ── */
.dot-menu {
  position: fixed;
  z-index: 9999;
  background: #fff;
  border: 1.5px solid $border;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(13, 27, 62, 0.12);
  padding: 4px;
  min-width: 130px;
}

.dot-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: transparent;
  font-size: 13px;
  font-family: $font;
  color: $dark;
  cursor: pointer;
  border-radius: 7px;
  text-align: left;
  transition: background 0.12s;

  i {
    font-size: 12px;
    color: #7a8499;
  }

  &:hover {
    background: #f4f6fb;
  }

  &--del {
    color: #b42318;

    i {
      color: #b42318;
    }

    &:hover {
      background: rgba(180, 35, 24, 0.07);
    }
  }
}

/* ── delete modal ── */
.del-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(13, 27, 62, 0.45);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.del-box {
  background: #fff;
  border-radius: 16px;
  padding: 32px 28px 24px;
  width: 360px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(13, 27, 62, 0.2);
}

.del-icon {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: rgba(180, 35, 24, 0.1);
  color: #b42318;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 14px;
}

.del-title {
  font-size: 16px;
  font-weight: 700;
  color: $dark;
  margin-bottom: 8px;
}

.del-sub {
  font-size: 13px;
  color: #7a8499;
  line-height: 1.5;
  margin-bottom: 24px;
}

.del-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.del-cancel {
  flex: 1;
  height: 38px;
  border-radius: 8px;
  border: 1.5px solid $border;
  background: #fff;
  color: $dark;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #f4f6fb;
  }
}

.del-confirm {
  flex: 1;
  height: 38px;
  border-radius: 8px;
  border: none;
  background: #b42318;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #8f1a12;
  }
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
